'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { NextIntlClientProvider } from 'next-intl'

import { setLocaleAction } from './locale.actions'
import { DEFAULT_LOCALE, LOCALES } from './locale.constants'
import { type Locale } from './locale.types'

type LocaleProviderProps = {
  children: React.ReactNode
  initialLocale: Locale
}

interface LocaleContextType {
  changeLocale: (newLocale: Locale) => void
  locale: Locale
}

const LocaleContext = React.createContext<LocaleContextType>({
  changeLocale: () => {},
  locale: DEFAULT_LOCALE,
})

const LocaleProvider = (props: LocaleProviderProps) => {
  const { children, initialLocale } = props

  const [locale, setLocale] = useState<Locale>(initialLocale)
  const [isLoading, setIsLoading] = useState(true)
  const [messages, setMessages] = useState({})

  useEffect(() => {
    let isMounted = true

    const initUserLocale = async () => {
      try {
        const translations = await import(`@/translation/${locale}.json`)
        await setLocaleAction(locale)
        if (isMounted) {
          setMessages(translations.default)
          setIsLoading(false)
        }
      } catch (error) {
        if (isMounted) {
          console.log('Failed to load user locale', error)
        }
      }
    }

    initUserLocale()

    return () => {
      isMounted = false
    }
  }, [locale])

  const changeLocale = useCallback(async (newLocale: Locale) => {
    if (!LOCALES.includes(newLocale)) {
      throw new Error(`Locale '${newLocale}' is not supported`)
    }

    try {
      const translationPromise = import(`@/translation/${newLocale}.json`)
      const saveLocalePromise = setLocaleAction(newLocale)
      const [importedTranslations] = await Promise.all([
        translationPromise,
        saveLocalePromise,
      ])
      setMessages(importedTranslations.default)
      setLocale(newLocale)
    } catch (error) {
      console.error(`Failed to change locale to ${newLocale}`, error)
    }
  }, [])

  const value = useMemo(
    () => ({
      changeLocale,
      locale,
    }),
    [changeLocale, locale],
  )

  if (isLoading) return null

  return (
    <NextIntlClientProvider locale={locale} messages={messages}>
      <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
    </NextIntlClientProvider>
  )
}

const useLocale = () => {
  const context = React.useContext(LocaleContext)
  if (context === undefined) {
    throw new Error('useLocale must be used within an LocaleProvider')
  }
  return context
}

export { LocaleProvider, useLocale }
